<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const store = useStore();
const { t } = useI18n();

const searchQuery = ref('');
const editingProductId = ref(null);
const deleteCandidate = ref({});
const showDeletePopup = ref(false);
const form = reactive({
  name: '',
  identifier: '',
  description: '',
  userIds: [],
  inboxIds: [],
});

const products = computed(() => store.getters['products/getProducts']);
const agents = computed(() => store.getters['agents/getAgents']);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const uiFlags = computed(() => store.getters['products/getUIFlags']);
const isSaving = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);
const isEditing = computed(() => editingProductId.value !== null);

const filteredProducts = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return products.value;

  return picoSearch(products.value, query, [
    'name',
    'identifier',
    'description',
  ]);
});

const compactNames = names => {
  if (!names.length) return t('PRODUCTS_SETTINGS.NONE_BOUND');

  const visibleNames = names.slice(0, 4).join(', ');
  const hiddenCount = names.length - 4;
  return hiddenCount > 0
    ? t('PRODUCTS_SETTINGS.BOUND_SUMMARY_MORE', {
        names: visibleNames,
        n: hiddenCount,
      })
    : visibleNames;
};

const productMemberNames = product => {
  return (product.members || [])
    .map(member => member.available_name || member.name || member.email)
    .filter(Boolean);
};

const productInboxNames = product => {
  return (product.inboxes || []).map(inbox => inbox.name).filter(Boolean);
};

const resetForm = () => {
  editingProductId.value = null;
  form.name = '';
  form.identifier = '';
  form.description = '';
  form.userIds = [];
  form.inboxIds = [];
};

const editProduct = product => {
  editingProductId.value = product.id;
  form.name = product.name || '';
  form.identifier = product.identifier || '';
  form.description = product.description || '';
  form.userIds = [...(product.member_ids || [])];
  form.inboxIds = [...(product.inbox_ids || [])];
};

const productPayload = () => ({
  name: form.name.trim(),
  identifier: form.identifier.trim() || null,
  description: form.description.trim(),
  user_ids: form.userIds,
  inbox_ids: form.inboxIds,
});

const refreshProductData = async () => {
  await Promise.all([
    store.dispatch('products/get'),
    store.dispatch('inboxes/get'),
  ]);
};

const submitProduct = async () => {
  if (!form.name.trim()) {
    useAlert(t('PRODUCTS_SETTINGS.FORM.NAME_REQUIRED'));
    return;
  }

  try {
    if (isEditing.value) {
      await store.dispatch('products/update', {
        id: editingProductId.value,
        ...productPayload(),
      });
      useAlert(t('PRODUCTS_SETTINGS.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('products/create', productPayload());
      useAlert(t('PRODUCTS_SETTINGS.CREATE_SUCCESS'));
    }
    resetForm();
    await refreshProductData();
  } catch {
    useAlert(t('PRODUCTS_SETTINGS.SAVE_ERROR'));
  }
};

const openDelete = product => {
  deleteCandidate.value = product;
  showDeletePopup.value = true;
};

const closeDelete = () => {
  deleteCandidate.value = {};
  showDeletePopup.value = false;
};

const confirmDeletion = async () => {
  try {
    await store.dispatch('products/delete', deleteCandidate.value.id);
    await refreshProductData();
    useAlert(t('PRODUCTS_SETTINGS.DELETE_SUCCESS'));
  } catch {
    useAlert(t('PRODUCTS_SETTINGS.DELETE_ERROR'));
  } finally {
    closeDelete();
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
  store.dispatch('products/get');
});
</script>

<template>
  <SettingsLayout
    class="w-full"
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('PRODUCTS_SETTINGS.LOADING')"
    :no-records-found="!products.length"
    :no-records-message="$t('PRODUCTS_SETTINGS.LIST_EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('PRODUCTS_SETTINGS.HEADER')"
        :description="$t('PRODUCTS_SETTINGS.DESCRIPTION')"
        :search-placeholder="$t('PRODUCTS_SETTINGS.SEARCH_PLACEHOLDER')"
      >
        <template v-if="products.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('PRODUCTS_SETTINGS.COUNT', { n: products.length }) }}
          </span>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #preBody>
      <form
        class="grid gap-4 pb-6 border-b border-n-weak lg:grid-cols-[minmax(0,1fr)_minmax(15rem,0.8fr)_minmax(15rem,0.8fr)]"
        @submit.prevent="submitProduct"
      >
        <section class="flex flex-col gap-3">
          <label
            class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
          >
            {{ $t('PRODUCTS_SETTINGS.FORM.NAME') }}
            <input
              v-model="form.name"
              class="w-full h-10 px-3 text-sm border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12"
              :placeholder="$t('PRODUCTS_SETTINGS.FORM.NAME_PLACEHOLDER')"
            />
          </label>
          <label
            class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
          >
            {{ $t('PRODUCTS_SETTINGS.FORM.IDENTIFIER') }}
            <input
              v-model="form.identifier"
              class="w-full h-10 px-3 text-sm border rounded-lg bg-n-alpha-2 border-n-weak text-n-slate-12"
              :placeholder="$t('PRODUCTS_SETTINGS.FORM.IDENTIFIER_PLACEHOLDER')"
            />
          </label>
          <label
            class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
          >
            {{ $t('PRODUCTS_SETTINGS.FORM.DESCRIPTION') }}
            <textarea
              v-model="form.description"
              class="w-full min-h-20 px-3 py-2 text-sm border rounded-lg resize-y bg-n-alpha-2 border-n-weak text-n-slate-12"
              :placeholder="
                $t('PRODUCTS_SETTINGS.FORM.DESCRIPTION_PLACEHOLDER')
              "
            />
          </label>
        </section>

        <section class="flex flex-col gap-2 min-w-0">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('PRODUCTS_SETTINGS.FORM.AGENTS') }}
          </span>
          <div class="overflow-auto border rounded-lg max-h-52 border-n-weak">
            <label
              v-for="agent in agents"
              :key="agent.id"
              class="flex items-center gap-2 px-3 py-2 text-sm border-b cursor-pointer border-n-weak last:border-b-0 text-n-slate-12"
            >
              <input
                v-model="form.userIds"
                type="checkbox"
                :value="agent.id"
                class="size-4"
              />
              <span class="min-w-0 truncate">
                {{ agent.available_name || agent.name || agent.email }}
              </span>
            </label>
          </div>
        </section>

        <section class="flex flex-col gap-2 min-w-0">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('PRODUCTS_SETTINGS.FORM.INBOXES') }}
          </span>
          <div class="overflow-auto border rounded-lg max-h-52 border-n-weak">
            <label
              v-for="inbox in inboxes"
              :key="inbox.id"
              class="flex items-center gap-2 px-3 py-2 text-sm border-b cursor-pointer border-n-weak last:border-b-0 text-n-slate-12"
            >
              <input
                v-model="form.inboxIds"
                type="checkbox"
                :value="inbox.id"
                class="size-4"
              />
              <span class="min-w-0 truncate">
                {{ inbox.name }}
              </span>
            </label>
          </div>
        </section>

        <div class="flex items-center gap-2 lg:col-span-3">
          <Button
            type="submit"
            size="sm"
            :is-loading="isSaving"
            :label="
              isEditing
                ? $t('PRODUCTS_SETTINGS.FORM.UPDATE')
                : $t('PRODUCTS_SETTINGS.FORM.CREATE')
            "
          />
          <Button
            v-if="isEditing"
            type="button"
            size="sm"
            slate
            :label="$t('PRODUCTS_SETTINGS.FORM.CANCEL')"
            @click="resetForm"
          />
        </div>
      </form>
    </template>

    <template #body>
      <span
        v-if="!filteredProducts.length && searchQuery"
        class="flex items-center justify-center flex-1 py-20 text-base text-center text-n-slate-11"
      >
        {{ $t('PRODUCTS_SETTINGS.NO_RESULTS') }}
      </span>

      <div v-else class="divide-y divide-n-weak">
        <article
          v-for="product in filteredProducts"
          :key="product.id"
          class="flex flex-col gap-3 py-4 md:flex-row md:items-start md:justify-between"
        >
          <div class="flex items-start min-w-0 gap-4">
            <div
              class="flex items-center justify-center flex-shrink-0 border rounded-lg size-10 border-n-weak"
            >
              <Icon icon="i-lucide-box" class="size-4 text-n-slate-11" />
            </div>
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <h2 class="mb-0 truncate text-heading-3 text-n-slate-12">
                  {{ product.name }}
                </h2>
                <span
                  v-if="product.identifier"
                  class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
                >
                  {{ product.identifier }}
                </span>
              </div>
              <p
                v-if="product.description"
                class="max-w-2xl mt-1 mb-0 text-sm text-n-slate-11"
              >
                {{ product.description }}
              </p>
              <div class="flex flex-wrap gap-2 mt-2 text-xs text-n-slate-11">
                <span>
                  {{
                    $t('PRODUCTS_SETTINGS.MEMBER_COUNT', {
                      n: product.member_ids?.length || 0,
                    })
                  }}
                </span>
                <span>
                  {{
                    $t('PRODUCTS_SETTINGS.INBOX_COUNT', {
                      n: product.inbox_ids?.length || 0,
                    })
                  }}
                </span>
              </div>
              <dl class="grid gap-1 mt-3 text-xs text-n-slate-11">
                <div class="flex min-w-0 gap-2">
                  <dt class="shrink-0 text-n-slate-12">
                    {{ $t('PRODUCTS_SETTINGS.BOUND_AGENTS') }}
                  </dt>
                  <dd class="min-w-0 mb-0 truncate">
                    {{ compactNames(productMemberNames(product)) }}
                  </dd>
                </div>
                <div class="flex min-w-0 gap-2">
                  <dt class="shrink-0 text-n-slate-12">
                    {{ $t('PRODUCTS_SETTINGS.BOUND_INBOXES') }}
                  </dt>
                  <dd class="min-w-0 mb-0 truncate">
                    {{ compactNames(productInboxNames(product)) }}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
          <div class="flex gap-2 md:justify-end">
            <Button
              v-tooltip.top="$t('PRODUCTS_SETTINGS.EDIT')"
              icon="i-woot-settings"
              slate
              sm
              @click="editProduct(product)"
            />
            <Button
              v-tooltip.top="$t('PRODUCTS_SETTINGS.DELETE')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDelete(product)"
            />
          </div>
        </article>
      </div>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('PRODUCTS_SETTINGS.DELETE_CONFIRM_TITLE')"
      :message="$t('PRODUCTS_SETTINGS.DELETE_CONFIRM_MESSAGE')"
      :confirm-text="$t('PRODUCTS_SETTINGS.DELETE_CONFIRM_YES')"
      :reject-text="$t('PRODUCTS_SETTINGS.DELETE_CONFIRM_NO')"
      :confirm-value="deleteCandidate.name"
      :confirm-place-holder-text="
        $t('PRODUCTS_SETTINGS.DELETE_CONFIRM_PLACEHOLDER', {
          productName: deleteCandidate.name,
        })
      "
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
